import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, PsvPermit, PsvPermitClass } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const BOARD_ROLES = ['PSV_BOARD'];
const APPLY_ROLES = ['PSV_BOARD', 'LTA_OFFICER'];
const CLASSES: PsvPermitClass[] = ['TAXI', 'HIRE_CAR', 'BUS', 'GOODS'];
const STATUSES = ['APPLIED', 'BOARD_REVIEW', 'APPROVED', 'REJECTED', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'EXPIRED'];

/** M21 — PSV (taxi/hire car/bus/goods) permits: apply, board review, decide (quota-gated), suspend/revoke. */
export function Psv() {
  const { persona } = useAuth();
  const canApply = persona ? persona.roles.some((r) => APPLY_ROLES.includes(r)) : false;
  const isBoard = persona ? persona.roles.some((r) => BOARD_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/transport/psv/permits?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<PsvPermit>>(path);

  return (
    <div>
      <PageHeader
        title="PSV Permits"
        subtitle="Taxi, hire car, bus, and goods-vehicle public service permits — board review and base+class quota (M21 — LTA)."
        actions={
          <>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            {canApply ? (
              <button className="btn-primary" onClick={() => setShowApply(true)}>
                + Apply for permit
              </button>
            ) : null}
          </>
        }
      />

      {!canApply ? <RoleNotice roles={APPLY_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No PSV permits match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Class</th>
                <th className="table-head">Operator</th>
                <th className="table-head">Base</th>
                <th className="table-head">Expires</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                  <td className="table-cell">{p.permitClass.replaceAll('_', ' ')}</td>
                  <td className="table-cell font-mono text-xs">{p.operatorRef}</td>
                  <td className="table-cell text-xs text-slate-500">{p.base}</td>
                  <td className="table-cell text-xs text-slate-500">{p.expiresAt ? formatDate(p.expiresAt) : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showApply ? (
        <ApplyPsvModal
          onClose={() => setShowApply(false)}
          onCreated={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <PsvDetailModal
          id={selectedId}
          isBoard={isBoard}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplyPsvModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [permitClass, setPermitClass] = useState<PsvPermitClass>('TAXI');
  const [operatorRef, setOperatorRef] = useState('');
  const [vehicleIds, setVehicleIds] = useState('');
  const [base, setBase] = useState('');
  const [route, setRoute] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/transport/psv/permits', {
        permitClass,
        operatorRef: operatorRef.trim(),
        vehicleIds: vehicleIds.split(',').map((s) => s.trim()).filter(Boolean),
        base: base.trim(),
        ...(route.trim() ? { route: route.trim() } : {}),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to apply for permit');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Apply for a PSV permit" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Permit class">
          <select className="field-input" value={permitClass} onChange={(e) => setPermitClass(e.target.value as PsvPermitClass)}>
            {CLASSES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Operator (NIN or business id)">
          <input className="field-input" value={operatorRef} onChange={(e) => setOperatorRef(e.target.value)} />
        </Field>
        <Field label="Vehicle ids (comma-separated)" className="col-span-2">
          <input className="field-input" value={vehicleIds} onChange={(e) => setVehicleIds(e.target.value)} placeholder="See Vehicle Registry detail for each vehicle's id" />
        </Field>
        <Field label="Base">
          <input className="field-input" value={base} onChange={(e) => setBase(e.target.value)} placeholder="Victoria" />
        </Field>
        <Field label="Route (optional)">
          <input className="field-input" value={route} onChange={(e) => setRoute(e.target.value)} />
        </Field>
      </div>
      <button
        className="btn-primary mt-4"
        disabled={busy || !operatorRef.trim() || !base.trim() || vehicleIds.split(',').map((s) => s.trim()).filter(Boolean).length === 0}
        onClick={submit}
      >
        {busy ? 'Applying…' : 'Submit application'}
      </button>
    </Modal>
  );
}

function PsvDetailModal({ id, isBoard, onClose, onChanged }: { id: string; isBoard: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<PsvPermit>(`/api/v1/transport/psv/permits/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [reason, setReason] = useState('');
  const [showSuspend, setShowSuspend] = useState(false);
  const [showRevoke, setShowRevoke] = useState(false);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const p = q.data;

  return (
    <Modal title={p ? `${p.permitClass.replaceAll('_', ' ')} permit — ${p.operatorRef}` : 'PSV permit'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Base" value={p.base} />
            <Detail label="Route" value={p.route ?? '—'} />
            <Detail label="Vehicles" value={p.vehicleIds.length} />
            <Detail label="Board decision" value={p.boardDecisionReason ?? '—'} />
            <Detail label="Expires" value={p.expiresAt ? formatDate(p.expiresAt) : '—'} />
          </div>

          {isBoard && p.status === 'APPLIED' ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/transport/psv/permits/${p.id}/review`))}>
              {busy ? 'Moving…' : 'Move to board review'}
            </button>
          ) : null}

          {isBoard && p.status === 'BOARD_REVIEW' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="flex gap-2">
                <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                  Approve
                </button>
                <button className={`btn-danger ${decision === 'REJECT' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('REJECT')}>
                  Reject
                </button>
              </div>
              {decision ? (
                <>
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <button
                    className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => call(() => api.post(`/api/v1/transport/psv/permits/${p.id}/decide`, { decision, reason: reason.trim() }))}
                  >
                    {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                  </button>
                  {decision === 'APPROVE' ? (
                    <p className="text-xs text-slate-400">Approval atomically consumes one unit of the base+class quota (falls back to 409 if exhausted).</p>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          {isBoard && p.status === 'ACTIVE' ? (
            !showSuspend ? (
              <button className="btn-danger" onClick={() => setShowSuspend(true)}>
                Suspend permit
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <Field label="Suspension reason">
                  <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <button
                  className="btn-danger"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => call(() => api.post(`/api/v1/transport/psv/permits/${p.id}/suspend`, { reason: reason.trim() }))}
                >
                  {busy ? 'Suspending…' : 'Confirm suspend'}
                </button>
              </div>
            )
          ) : null}

          {isBoard && (p.status === 'ACTIVE' || p.status === 'SUSPENDED') ? (
            !showRevoke ? (
              <button className="btn-danger" onClick={() => setShowRevoke(true)}>
                Revoke permit
              </button>
            ) : (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <Field label="Revocation reason">
                  <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <button
                  className="btn-danger"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => call(() => api.post(`/api/v1/transport/psv/permits/${p.id}/revoke`, { reason: reason.trim() }))}
                >
                  {busy ? 'Revoking…' : 'Confirm revoke'}
                </button>
              </div>
            )
          ) : null}

          {!isBoard ? <RoleNotice roles={BOARD_ROLES} /> : null}
        </div>
      ) : null}
    </Modal>
  );
}
